import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Phone, Loader2 } from 'lucide-react';
import { PhoneFormData } from '../../types';
import { useAuthStore } from '../../stores/authStore';

interface PhoneLoginFormProps {
  onOtpSent: (phoneNumber: string) => void;
}

export const PhoneLoginForm: React.FC<PhoneLoginFormProps> = ({ onOtpSent }) => {
  const { requestOtp, isLoading, error, clearError } = useAuthStore();
  const [phoneNumber, setPhoneNumber] = useState('+91');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<PhoneFormData>({
    defaultValues: {
      phoneNumber: '+91',
    },
  });

  // Watch the phone number field
  const watchedPhoneNumber = watch('phoneNumber');

  // Update local state when form value changes
  useEffect(() => {
    setPhoneNumber(watchedPhoneNumber || '+91');
  }, [watchedPhoneNumber]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Always ensure +91 prefix
    if (!value.startsWith('+91')) {
      setValue('phoneNumber', '+91');
      setPhoneNumber('+91');
      return;
    }
    
    // Remove any non-digit characters except the +91 prefix
    const digitsOnly = value.replace(/[^\d]/g, '');
    const formattedValue = '+91' + digitsOnly.slice(2); // Remove the 91 from digits and add +91
    
    // Limit to 10 digits after +91
    if (formattedValue.length <= 13) { // +91 + 10 digits = 13 characters
      setValue('phoneNumber', formattedValue);
      setPhoneNumber(formattedValue);
    }
  };

  const onSubmit = async (data: PhoneFormData) => {
    clearError();
    
    try {
      await requestOtp(data.phoneNumber);
      onOtpSent(data.phoneNumber);
    } catch (error) {
      // Error is handled by the store
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="card p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-600">
            Enter your phone number to continue
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Phone Number Field */}
          <div>
            <label 
              htmlFor="phoneNumber" 
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Indian Phone Number
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-gray-400" />
              </div>
              <input
                {...register('phoneNumber', {
                  required: 'Phone number is required',
                  pattern: {
                    value: /^\+91[6-9]\d{9}$/,
                    message: 'Please enter a valid 10-digit Indian phone number',
                  },
                  minLength: {
                    value: 13,
                    message: 'Phone number must be 10 digits',
                  },
                })}
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={handlePhoneChange}
                className={`input-field pl-10 ${errors.phoneNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="+91xxxxxxxxxx"
                maxLength={13}
                aria-invalid={errors.phoneNumber ? 'true' : 'false'}
                aria-describedby={errors.phoneNumber ? 'phone-error' : undefined}
              />
            </div>
            {errors.phoneNumber && (
              <p id="phone-error" className="mt-1 text-sm text-red-600" role="alert">
                {errors.phoneNumber.message}
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="bg-red-50 border border-red-200 rounded-lg p-4"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center"
            aria-describedby={isLoading ? 'loading-text' : undefined}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                <span id="loading-text">Sending OTP...</span>
              </>
            ) : (
              'Send OTP'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            We'll send you a verification code via SMS to your Indian number
          </p>
        </div>
      </div>
    </div>
  );
};
